import { Injectable } from '@angular/core';
import {withUtm} from '../utils/with-utm';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  CreateGroupCompetitionRequest,
  GroupCompetitionDto,
  GroupDetailDto,
  GroupHabitTemplateDto,
  GroupHabitTemplateInput,
  GroupHabitTemplatePushResultDto,
  GroupMemberDto,
  GroupMembershipSummaryDto,
  RosterRowDto
} from './GroupDto';

/**
 * Wraps every authenticated `/group/**` route for the group hub and team
 * challenge screens (backlog #304, #305). The public/anonymous invite
 * preview and join-by-token routes stay on `GroupInviteService`, which the
 * `/group/join` page keeps using unchanged.
 */
@Injectable({
  providedIn: 'root'
})
export class GroupService {

  constructor(private http: HttpClient) {}

  /** `GET /group/mine`: every group the caller belongs to, for the /groups list. */
  getMine(): Observable<GroupMembershipSummaryDto[]> {
    return this.http.get<GroupMembershipSummaryDto[]>(`${environment.apiURL}/group/mine`).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /** `GET /group/{id}`: full detail, `inviteToken` populated only for the OWNER. */
  getDetail(id: number): Observable<GroupDetailDto> {
    return this.http.get<GroupDetailDto>(`${environment.apiURL}/group/${id}`).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * `GET /group/{id}/members`: 403 for a MEMBER caller on a COACH group
   * (backend rule change in #304); TEAM groups allow any member.
   */
  getMembers(id: number): Observable<GroupMemberDto[]> {
    return this.http.get<GroupMemberDto[]>(`${environment.apiURL}/group/${id}/members`).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /** `POST /group/{id}/invite/regenerate`: OWNER only; invalidates the previous token. */
  regenerateInvite(id: number): Observable<{ inviteToken: string }> {
    return this.http.post<{ inviteToken: string }>(
      `${environment.apiURL}/group/${id}/invite/regenerate`, null
    ).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /** `POST /group/{id}/invite/email`: OWNER only; 409 on seat cap or mail failure. */
  sendInviteEmail(id: number, email: string): Observable<HttpResponse<any>> {
    return this.http.post(
      `${environment.apiURL}/group/${id}/invite/email`, { email },
      { observe: 'response', responseType: 'text' }
    ).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /** `DELETE /group/{id}/member/{userId}`: OWNER only. */
  removeMember(id: number, userId: string): Observable<HttpResponse<any>> {
    return this.http.delete(
      `${environment.apiURL}/group/${id}/member/${userId}`,
      { observe: 'response', responseType: 'text' }
    ).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /** `POST /group/{id}/leave`: 409 when the caller is the OWNER. */
  leave(id: number): Observable<HttpResponse<any>> {
    return this.http.post(
      `${environment.apiURL}/group/${id}/leave`, null,
      { observe: 'response', responseType: 'text' }
    ).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /** `GET /group/{id}/competitions`: newest `startDate` first, any member. */
  getCompetitions(id: number): Observable<GroupCompetitionDto[]> {
    return this.http.get<GroupCompetitionDto[]>(`${environment.apiURL}/group/${id}/competitions`).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * `POST /group/{id}/competition`: OWNER only; 409 when the group already
   * has an UPCOMING/ACTIVE challenge, 400 on validation.
   */
  createCompetition(id: number, request: CreateGroupCompetitionRequest): Observable<GroupCompetitionDto> {
    return this.http.post<GroupCompetitionDto>(
      `${environment.apiURL}/group/${id}/competition`, request
    ).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /** `POST /group/{id}/competition/{competitionId}/join`: idempotent, any member. */
  joinCompetition(id: number, competitionId: number): Observable<GroupCompetitionDto> {
    return this.http.post<GroupCompetitionDto>(
      `${environment.apiURL}/group/${id}/competition/${competitionId}/join`, null
    ).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * `GET /group/{id}/roster`: OWNER only, one row per client (backlog #306).
   * 403 for a MEMBER caller, and 403 with a `GROUP_NOT_ENTITLED` body when the
   * group is not activated. Rows arrive sorted quietest first.
   */
  getRoster(id: number): Observable<RosterRowDto[]> {
    return this.http.get<RosterRowDto[]>(`${environment.apiURL}/group/${id}/roster`).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * `GET /group/{id}/habits`: the group's habit template in position order,
   * `[]` when the group has none. Any member may read it. There is no
   * entitlement gate on the read (backlog #328).
   */
  getHabitTemplate(id: number): Observable<GroupHabitTemplateDto[]> {
    return this.http.get<GroupHabitTemplateDto[]>(`${environment.apiURL}/group/${id}/habits`).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * `PUT /group/{id}/habits`: OWNER only, full replacement of the template,
   * returns the stored rows. 400 carries `{"error": "<message>"}` on
   * validation (the consumer habit rules, plus at most 20 habits), 403 for a
   * non-owner and for a body carrying `GROUP_NOT_ENTITLED`. Saving never
   * writes into anybody's Notion tracker; that is `pushHabitTemplate`.
   */
  saveHabitTemplate(
    id: number,
    habits: GroupHabitTemplateInput[]
  ): Observable<GroupHabitTemplateDto[]> {
    return this.http.put<GroupHabitTemplateDto[]>(
      `${environment.apiURL}/group/${id}/habits`, habits
    ).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * `POST /group/{id}/habits/push`: OWNER only, synchronous, MEMBER rows only.
   * Writes the stored template additively into each member's own tracker and
   * reports per member what was added, what already existed, what failed.
   */
  pushHabitTemplate(id: number): Observable<GroupHabitTemplatePushResultDto> {
    return this.http.post<GroupHabitTemplatePushResultDto>(
      `${environment.apiURL}/group/${id}/habits/push`, null
    ).pipe(
      catchError((error) => throwError(() => error))
    );
  }

  /**
   * Builds the public join link shown to the OWNER for copy/share, following
   * the same hardcoded-origin pattern as
   * `CompetitionComponent.buildInviteLink` and `DuelComponent` (no shared
   * public-origin helper exists in this codebase today).
   */
  buildJoinLink(inviteToken: string): string {
    return withUtm(`https://notionhabitheroes.com/group/join?token=${inviteToken}`, 'group-invite', 'share');
  }
}
